import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Users, Activity, DollarSign, Zap, TrendingUp, Sparkles, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function Stat({ icon: Icon, label, value, sub, tone = "cyan" }) {
  const map = { cyan: "text-cyan-signal", emerald: "text-emerald-400", amber: "text-amber-400", purple: "text-purple-400" };
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className={`w-4 h-4 ${map[tone]}`} />
        <span className="font-mono text-xs uppercase tracking-widest">{label}</span>
      </div>
      <div className={`text-3xl sm:text-4xl font-display font-bold tracking-tight mt-2 ${map[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [config, setConfig] = useState(null);
  const [blasting, setBlasting] = useState(false);
  const [blastResult, setBlastResult] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/login");
    if (user && !user.is_admin) nav("/dashboard");
  }, [user, loading, nav]);

  useEffect(() => {
    if (user?.is_admin) {
      const refresh = () => {
        api.get("/admin/overview").then(({ data }) => setData(data)).catch((e) => setErr(e.message));
        api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
      };
      refresh();
      const id = setInterval(refresh, 15000);
      return () => clearInterval(id);
    }
  }, [user]);

  const runBlast = async () => {
    if (!config?.stripe_live) {
      if (!window.confirm("Stripe isn't detected as live yet. Send the launch email anyway?")) return;
    }
    setBlasting(true);
    setBlastResult(null);
    try {
      const { data } = await api.post("/admin/waitlist/blast", { app_url: window.location.origin });
      setBlastResult(data);
    } catch (e) {
      setBlastResult({ error: e?.response?.data?.detail || e.message });
    } finally { setBlasting(false); }
  };

  if (loading || !user || !user.is_admin) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-signal" /></div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-signal" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-overview">
      <div className="mb-8">
        <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">Founder view</span>
        <h1 className="mt-1 text-3xl sm:text-4xl font-display font-bold tracking-tight">Command center.</h1>
        <p className="mt-2 text-slate-400 text-sm">Live platform-wide stats · refreshes every 15s</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat icon={Users}     label="Total signups"       value={data.total_users.toLocaleString()}                sub={`${data.promo_claimed}/${data.promo_cap} launch promos claimed`} tone="cyan" />
        <Stat icon={Activity}  label="Verifications served" value={data.total_lookups.toLocaleString()}              sub="all-time" tone="emerald" />
        <Stat icon={DollarSign} label="Revenue"             value={`$${data.total_revenue_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} sub={`${data.total_paid_transactions} paid sub${data.total_paid_transactions === 1 ? "" : "s"}`} tone="amber" />
        <Stat icon={TrendingUp} label="Paid plan users"     value={((data.by_plan.growth || 0) + (data.by_plan.professional || 0) + (data.by_plan.enterprise || 0)).toLocaleString()} sub={`waitlist: ${(data.waitlist_count || 0).toLocaleString()}`} tone="purple" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
          <h3 className="font-display font-semibold flex items-center gap-2 mb-4"><Zap className="w-4 h-4 text-cyan-signal" /> Verifications by type</h3>
          <div className="space-y-3">
            {["phone", "email", "ip"].map((k) => {
              const v = data.by_kind[k] || 0;
              const pct = data.total_lookups > 0 ? (v / data.total_lookups) * 100 : 0;
              return (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize">{k}</span>
                    <span className="font-mono text-slate-400">{v.toLocaleString()} <span className="text-slate-600">({pct.toFixed(0)}%)</span></span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-cyan-signal" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
          <h3 className="font-display font-semibold flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-cyan-signal" /> Plan mix</h3>
          <div className="space-y-3">
            {["starter", "growth", "professional", "enterprise"].map((p) => {
              const v = data.by_plan[p] || 0;
              const pct = data.total_users > 0 ? (v / data.total_users) * 100 : 0;
              return (
                <div key={p}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize">{p}</span>
                    <span className="font-mono text-slate-400">{v.toLocaleString()} <span className="text-slate-600">({pct.toFixed(0)}%)</span></span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className={`h-full ${p === "starter" ? "bg-slate-500" : p === "growth" ? "bg-cyan-signal" : p === "professional" ? "bg-purple-400" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="admin-recent-users">
          <h3 className="font-display font-semibold mb-4">Recent signups</h3>
          {data.recent_users.length === 0 ? <p className="text-sm text-slate-500">No signups yet.</p> : (
            <div className="space-y-2">
              {data.recent_users.map((u) => (
                <div key={u._id} className="flex items-center justify-between text-sm rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.name || u.email.split("@")[0]}</div>
                    <div className="text-xs font-mono text-slate-500 truncate">{u.email}</div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <div className="text-[10px] font-mono uppercase text-cyan-signal">{u.plan || "starter"}</div>
                    <div className="text-xs text-slate-500 font-mono">{new Date(u.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="admin-recent-paid">
          <h3 className="font-display font-semibold mb-4">Recent payments</h3>
          {data.recent_paid.length === 0 ? <p className="text-sm text-slate-500">No paid subscriptions yet. Once someone completes checkout it will appear here.</p> : (
            <div className="space-y-2">
              {data.recent_paid.map((tx) => (
                <div key={tx._id} className="flex items-center justify-between text-sm rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{tx.user_email}</div>
                    <div className="text-xs font-mono text-slate-500">{tx.plan} · {tx.interval || "monthly"}</div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <div className="font-mono text-emerald-400">${(tx.amount / 100).toFixed(2)}</div>
                    <div className="text-xs text-slate-500 font-mono">{new Date(tx.updated_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-cyan-500/20 bg-gradient-to-br from-slate-900 to-cyan-950/20 p-6" data-testid="admin-launch-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-cyan-signal" />
              <h3 className="font-display font-semibold">Launch controls</h3>
            </div>
            <div className="text-sm text-slate-400 mt-1 space-y-1">
              <p>
                Stripe:{" "}
                {config?.stripe_live ? (
                  <span className="inline-flex items-center gap-1 text-emerald-400 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> LIVE — accepting real payments</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-400 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> test mode (auto-swaps on next deploy)</span>
                )}
              </p>
              <p>Waitlist: <span className="font-mono text-slate-200">{data.waitlist_count}</span> total · <span className="font-mono text-cyan-signal">{data.waitlist_pending || 0}</span> not yet notified</p>
            </div>
          </div>
          <Button
            data-testid="btn-waitlist-blast"
            onClick={runBlast}
            disabled={blasting || (data.waitlist_pending || 0) === 0}
            className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold"
          >
            {blasting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Email waitlist ({data.waitlist_pending || 0})</>}
          </Button>
        </div>
        {blastResult && (
          <div className={`mt-4 rounded-lg border p-3 text-sm ${blastResult.error ? "border-rose-500/30 bg-rose-500/5 text-rose-300" : "border-emerald-500/30 bg-emerald-500/5 text-emerald-300"}`}>
            {blastResult.error ? `Blast failed: ${blastResult.error}` : `Sent ${blastResult.sent} · failed ${blastResult.failed}`}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-4">Share <Link to="/launch" className="text-cyan-signal hover:underline">/launch</Link> and <Link to="/compare" className="text-cyan-signal hover:underline">/compare</Link> on Product Hunt, X, and Hacker News. Signups + payments show up here live.</p>
      </div>
    </div>
  );
}
